import { AttributeComparators } from 'collections/attributeComparators';
import {
  JsonFilterPrefixes,
  encodingIdsToFilterNames,
  filterNamesToEncodingIds,
} from 'models/filterEnums';
import {
  BooleanOperator,
  FilterTypes,
  type FilterAttributes,
  type FormattingOperator,
} from '@ardoq/api-types';
import { logWarn } from '@ardoq/logging';
import { assign, isUndefined } from 'lodash';
import { createSystemFilter } from 'models/utils/filterUtils';

const TYPE_NAMES_PREFIX = '__typeNames_v1__:';

const getCommonFilterAttributes = (str: string) => {
  const parser = {
    regexp: /(?:f_)(r_)?(p_)?(c_[a-f\d]{6}_)?/i,
    affectReferenceGroup: 1,
    affectComponentGroup: 2,
    colorGroup: 3,
  };
  const match = parser.regexp.exec(str);

  return match
    ? {
        affectReference: !!match[parser.affectReferenceGroup],
        affectComponent: !!match[parser.affectComponentGroup],
        color:
          match[parser.colorGroup] &&
          `#${match[parser.colorGroup].replace('c_', '').replace('_', '')}`,
      }
    : {};
};

const getTagFilterAttributes = (str: string) => {
  const parser = {
    regexp: /(?:t_)(!)?([^&]*)(?:&)?/i,
    isNegativeGroup: 1,
    nameGroup: 2,
  };
  const match = parser.regexp.exec(str);

  return match
    ? {
        isNegative: !isUndefined(match[parser.isNegativeGroup]),
        name: match[parser.nameGroup],
        value: match[parser.nameGroup],
        type: 'tag',
      }
    : {};
};

const getAttributeFilterAttributes = (str: string) => {
  const parser = {
    regexp: /(!)?(?:@)([^=<>]+)(==|=|<|>)([^&]+)(?:&)?/i,
    isNegativeGroup: 1,
    nameGroup: 2,
    operatorGroup: 3,
    valueGroup: 4,
  };

  const match = parser.regexp.exec(str);

  if (match) {
    // Deciding to never allow just plain (=) value in a contains check,
    // as Is empty filter now will never work.
    if (
      match[parser.valueGroup] === '=' &&
      match[parser.operatorGroup] === '='
    ) {
      match[parser.valueGroup] = '';
      match[parser.operatorGroup] = '==';
    }
    const comparator = AttributeComparators.getByOperator(
      match[parser.operatorGroup],
      match[parser.nameGroup]
    );

    return {
      isNegative: !isUndefined(match[parser.isNegativeGroup]),
      name: match[parser.nameGroup],
      comparator: (comparator || {}).id,
      value: match[parser.valueGroup],
      type: 'attribute',
    };
  }
  return {};
};

const comparators = [
  '',
  'greaterThan',
  'lessThan',
  'contains',
  'equals',
  'dateMatch',
  'dateLater',
  'dateEarlier',
  'idMatch',
  'boolean',
  'dateRangeContains',
  'dateRangeDoesNotContain',
  'dateRangeBefore',
  'dateRangeAfter',
];

// Extended comparators for version >= 1 (NEW_CONDITIONAL_FORMATTING)
// When version >= 1, we use bits 6-10 for comparator (5 bits = 32 values)
const extendedComparators = [
  ...comparators,
  'idDoesNotMatch',
  'notEqual',
  'notContains',
  'notBoolean',
  'greaterThanOrEqualTo',
  'lessThanOrEqualTo',
  'between',
  'notBetween',
  'isEmpty',
  'listContains',
  'listDoesNotContain',
  'unset', // When we confirm that unset is not using anymore - we will be able to replace it by dummy string.
  // If we just remove it, it will affect next in the array comparators (e.x. 'relativeDate')
  'relativeDate',
];

const comparatorToEnum = (comparator = '', version = 0) =>
  version >= 1
    ? extendedComparators.indexOf(comparator)
    : comparators.indexOf(comparator);
const enumToComparator = (enum_ = 0, version = 0) =>
  version >= 1 ? extendedComparators[enum_] : comparators[enum_];
const typeToEnum = (type?: FilterTypes) => (type === 'attribute' ? 1 : 0);
const enumToType = (enum_: number) =>
  enum_ === 1 ? FilterTypes.ATTRIBUTE : FilterTypes.TAG;
const dateRangeToEnum = (type?: FilterTypes) =>
  type === FilterTypes.DATE_RANGE ? 1 : 0;
const conditionToEnum = (condition?: string) =>
  condition === BooleanOperator.AND ? 1 : 0;
const enumToCondition = (enum_: number) =>
  enum_ === 1 ? BooleanOperator.AND : BooleanOperator.OR;
const nameToEnum = (name: string) => filterNamesToEncodingIds[name] || name;
/** @param {string | number} indexOrName */
const enumToName = (indexOrName: string | number) => {
  if (!Number.isInteger(indexOrName)) return indexOrName;
  return encodingIdsToFilterNames[indexOrName as number];
};

export const defaultFilterToArray = ({
  affectComponent,
  affectReference,
  comparator,
  isNegative,
  name,
  type,
  value,
  color,
  rules,
  condition,
  version,
  affectsTypeName,
  affectsTypeNames,
}: FilterAttributes) => {
  // To ensure backwards filter URL compatibility, maintaining a value
  // for isEnabled even though it's no longer a concept we use.
  const isEnabled = true;
  const versionValue = version || 0;
  const comparatorEnum = comparatorToEnum(comparator, versionValue);

  // For version >= 1, use 5 bits for comparator (bits 6-10)
  // For version 0, use 4 bits for comparator (bits 6-9)
  const comparatorBits =
    versionValue >= 1 ? comparatorEnum << 6 : (comparatorEnum & 0b1111) << 6;
  const dateRangeBit =
    versionValue >= 1
      ? dateRangeToEnum(type) << 11
      : dateRangeToEnum(type) << 10;
  const versionBits = versionValue << 12;

  const arr = [
    ((affectComponent ? 1 : 0) << 0) |
      ((affectReference ? 1 : 0) << 1) |
      ((isEnabled ? 1 : 0) << 2) |
      ((isNegative ? 1 : 0) << 3) |
      (typeToEnum(type) << 4) |
      (conditionToEnum(condition) << 5) |
      comparatorBits |
      dateRangeBit |
      versionBits,
    nameToEnum(name!),
    value,
  ];
  if (color) {
    arr.push(encodeURIComponent(color));
  }
  if (rules) {
    if (arr.length === 3) {
      arr.push('');
    }
    const arrayEntries = rules.map(defaultFilterToArray);
    // @ts-expect-error this is suspicious, but I'm not going to change it right now. was the intent to flatten the array before pushing it using spread syntax?
    arr.push(arrayEntries);
  }

  if (affectsTypeNames?.length) {
    arr[5] = `${TYPE_NAMES_PREFIX}${JSON.stringify(affectsTypeNames)}`;
  } else if (affectsTypeName) {
    arr[5] = affectsTypeName;
  }
  return arr;
};

const defaultArrayToFilter = ([
  bitflags,
  name,
  value,
  color,
  rules,
  affectsTypeName,
]: Array<any>) => {
  const affectComponent = Boolean((bitflags >> 0) & 1);
  const affectReference = Boolean((bitflags >> 1) & 1);
  // isEnabled bit is here, but is no longer used in Ardoq
  const isNegative = Boolean((bitflags >> 3) & 1);

  // Version determines bit layout
  // Version 0 (or missing): comparator at bits 6-9, dateRange at bit 10
  // Version >= 1: comparator at bits 6-10, dateRange at bit 11, version at bits 12-14
  const version = (bitflags >> 12) & 0b111;
  const comparatorEnum =
    version >= 1
      ? (bitflags >> 6) & 0b11111 // 5 bits for extended comparators
      : (bitflags >> 6) & 0b1111; // 4 bits for legacy comparators
  const isDateRange =
    version >= 1
      ? Boolean((bitflags >> 11) & 1)
      : Boolean((bitflags >> 10) & 1);

  const rawComparator = enumToComparator(comparatorEnum, version);
  // Old URLs may have encoded the 'unset' comparator, which no
  // longer exists. Treat it as 'isEmpty', which is the closest equivalent.
  if (rawComparator === 'unset') {
    logWarn(
      new Error('Decoded deprecated "unset" comparator from URL'),
      'Deprecated "unset" comparator found in URL — remapping to "isEmpty"',
      { rawComparator, comparatorEnum, version }
    );
  }
  const comparator = (
    rawComparator === 'unset' ? 'isEmpty' : rawComparator
  ) as FormattingOperator;

  const type: FilterTypes = isDateRange
    ? FilterTypes.DATE_RANGE
    : enumToType((bitflags >> 4) & 1);

  // Slot 5 is either a plain type name (single-type, backward-compat)
  // or a "__typeNames_v1__:[...]" prefixed string for multi-type rules.
  const typeNameSlot = affectsTypeName as string | undefined;
  const parsedTypeNames = (() => {
    if (!typeNameSlot?.startsWith(TYPE_NAMES_PREFIX)) return null;
    try {
      return JSON.parse(
        typeNameSlot.slice(TYPE_NAMES_PREFIX.length)
      ) as string[];
    } catch {
      return null;
    }
  })();

  const dict: FilterAttributes = {
    affectComponent,
    affectReference,
    isNegative,
    type,
    comparator,
    name: enumToName(name) as string,
    value,
    version,
    // Plain string in slot 5 is a legacy single-type URL — normalize to affectsTypeNames immediately
    // so nothing downstream sees affectsTypeName from this path. This detection logic stays even
    // after the BE migration; old bookmarked URLs must keep working indefinitely.
    ...(parsedTypeNames
      ? { affectsTypeNames: parsedTypeNames }
      : typeNameSlot
        ? { affectsTypeNames: [typeNameSlot] }
        : {}),
  };
  if (color) {
    dict.color = decodeURIComponent(color);
  }
  if (rules) {
    dict.rules = rules.map((rule: Array<any>) => defaultArrayToFilter(rule));
    dict.condition = enumToCondition((bitflags >> 5) & 1);
  }
  return dict;
};

export const labelFormattingFilterToArray = ({
  affectReference,
  includeTime,
  value,
  affectsTypeName,
  showFieldName,
}: FilterAttributes) => {
  const type = affectReference ? 1 : 0;
  // To ensure backwards filter URL compatibility, maintaining a value
  // for isEnabled even though it's no longer a concept we use.
  const enabled = 1;
  const includedTime = [includeTime ? 1 : 0];
  const values = (value as string).split(',');
  return [
    type,
    enabled,
    values,
    ...includedTime,
    affectsTypeName,
    showFieldName ? 1 : 0,
  ];
};

/**
 * Converts a system filter to an array for URL encoding.
 *
 * System filters only need to encode affectComponent, affectReference, and name, as the other attributes are either fixed or not relevant for system filters.
 * Can be extended in the future.
 */
export const systemFilterToArray = ({
  affectComponent,
  affectReference,
  name, // Must be a SystemFilterType enum for system filters
}: FilterAttributes) => {
  return [affectComponent ? 1 : 0, affectReference ? 1 : 0, name];
};

const systemFilterArrayToFilter = ([
  affectComponent,
  affectReference,
  name,
]: Array<any>): FilterAttributes =>
  createSystemFilter(Boolean(affectComponent), Boolean(affectReference), name);

// Element in pos. 1 is isEnabled, which is no longer used in filters
const labelFormattingArrayToFilter = ([
  type,
  ,
  values,
  isTimeIncluded,
  affectsTypeName,
  showFieldName,
]: Array<any>) => {
  const affectComponent = type === 0;
  const affectReference = type === 1;
  const includedTime = { includeTime: isTimeIncluded === 1 };
  const value = /** @type {string[]} */ values.join(',');
  return {
    affectComponent,
    affectReference,
    type: affectReference
      ? FilterTypes.REFERENCE_LABEL
      : FilterTypes.COMPONENT_LABEL,
    value,
    ...includedTime,
    affectsTypeName,
    showFieldName: showFieldName === 1,
  };
};

// JsonPrefixToFilter must be instantiated after the functions it keeps a map of
const JsonPrefixToFilter: Record<string, (arg: any) => FilterAttributes> = {
  [JsonFilterPrefixes.DEFAULT]: defaultArrayToFilter,
  [JsonFilterPrefixes.WORKSPACE_FILTER]: defaultArrayToFilter,
  [JsonFilterPrefixes.LABEL_FORMATTING]: labelFormattingArrayToFilter,
  [JsonFilterPrefixes.SYSTEM_FILTER]: systemFilterArrayToFilter,
};

export const parseUriEncoded = (str: string) => {
  const isTagFilter = str.match('_t_');
  const prefixMatch = Object.keys(JsonPrefixToFilter).find(prefix =>
    str.startsWith(prefix)
  );
  if (isTagFilter) {
    return assign(getCommonFilterAttributes(str), getTagFilterAttributes(str));
  } else if (prefixMatch) {
    return JsonPrefixToFilter[prefixMatch](
      JSON.parse(str.slice(prefixMatch.length))
    );
  }
  return assign(
    getCommonFilterAttributes(str),
    getAttributeFilterAttributes(str)
  );
};
